import React, { useEffect } from 'react';
import { PMNode, Process } from '../../types';
import type { Segment } from '../../migrations/legacyEdge';
import { buildEdgeMap, resolveProcessEdges } from '../../utils/processEdges';

interface TutorialSectionProps {
  nodes: Record<string, PMNode>;
  processes: Process[];
  edges: Segment[];
  pseudocodeExported: boolean;
  pngExported: boolean;
  onReset: () => void;
  onClose: () => void;
  onCompletionChange?: (done: boolean) => void;
}

interface TutorialChecklist {
  knowsRightClick: boolean;
  hasRole: boolean;
  hasTool: boolean;
  hasSoftware: boolean;
  hasChannel: boolean;
  hasProcess: boolean;
  hasFlow: boolean;
  hasConnectionVerb: boolean;
  hasPseudocode: boolean;
  hasPNG: boolean;
}

const stepStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  fontFamily: "'DM Mono',monospace",
  fontSize: 10,
  lineHeight: 1.45,
  color: '#6d6458',
};

const badgeStyle = (done: boolean): React.CSSProperties => ({
  width: 18,
  height: 18,
  borderRadius: 999,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: done ? '#e8f6ee' : '#f3f1ec',
  border: `1px solid ${done ? '#8fc8a9' : '#d8d2c8'}`,
  color: done ? '#2d7a50' : '#978f84',
  fontSize: 11,
  fontWeight: 700,
  flexShrink: 0,
  marginTop: 1,
});

const buildChecklist = (
  nodes: Record<string, PMNode>,
  processes: Process[],
  edges: Segment[],
  pseudocodeExported: boolean,
  pngExported: boolean
): TutorialChecklist => {
  const allNodes = Object.values(nodes);
  const bySubtype = (subtype: PMNode['subtype']) => allNodes.filter(n => n.subtype === subtype);

  return {
    knowsRightClick: true,
    hasRole: bySubtype('role').length > 0 || bySubtype('person').length > 0,
    hasTool: bySubtype('tool').length > 0,
    hasSoftware: bySubtype('software').length > 0,
    hasChannel: bySubtype('channel').length > 0,
    hasProcess: processes.length > 0,
    hasFlow: hasTutorialFlow(processes, edges, nodes),
    hasConnectionVerb: hasSegmentVerb(processes, edges),
    hasPseudocode: pseudocodeExported,
    hasPNG: pngExported,
  };
};

const hasTutorialFlow = (processes: Process[], edges: Segment[], nodes: Record<string, PMNode>): boolean => {
  const edgeMap = buildEdgeMap(edges);
  for (const proc of processes) {
    const segs = resolveProcessEdges(proc, edgeMap);
    if (!segs.length) continue;

    const edges = new Map<string, string[]>();
    for (const seg of segs) {
      const arr = edges.get(seg.from) ?? [];
      arr.push(seg.to);
      edges.set(seg.from, arr);
    }

    for (const [id, node] of Object.entries(nodes)) {
      if (node.subtype !== 'role' && node.subtype !== 'person') continue;
      const lv1 = edges.get(id) ?? [];
      for (const toolId of lv1) {
        if (nodes[toolId]?.subtype !== 'tool') continue;
        const lv2 = edges.get(toolId) ?? [];
        for (const swId of lv2) {
          if (nodes[swId]?.subtype !== 'software') continue;
          const lv3 = edges.get(swId) ?? [];
          for (const channelId of lv3) {
            if (nodes[channelId]?.subtype === 'channel') return true;
          }
        }
      }
    }
  }
  return false;
};

const hasSegmentVerb = (processes: Process[], edges: Segment[]): boolean =>
  processes.some(proc =>
    resolveProcessEdges(proc, buildEdgeMap(edges)).some(seg => (seg.label ?? '').trim().length > 0)
  );

const TutorialSection: React.FC<TutorialSectionProps> = ({
  nodes,
  processes,
  edges,
  pseudocodeExported,
  pngExported,
  onReset,
  onClose,
  onCompletionChange,
}) => {
  const checks = buildChecklist(nodes, processes, edges, pseudocodeExported, pngExported);
  const doneCount = Object.values(checks).filter(Boolean).length;
  const total = Object.keys(checks).length;
  const doneAll = doneCount === total;

  useEffect(() => {
    onCompletionChange?.(doneAll);
  }, [doneAll, onCompletionChange]);

  return (
    <div style={{
      border: '1px solid #e8e1d5',
      background: '#fffdf8',
      borderRadius: 10,
      padding: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <p style={{ margin: 0, fontSize: 10, color: '#8e8679', textTransform: 'uppercase', letterSpacing: '.07em' }}>
          Modo tutorial
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            onClick={onReset}
            style={{
              border: '1px solid #e3dbce',
              background: '#fff',
              color: '#7b7266',
              borderRadius: 999,
              fontSize: 10,
              padding: '3px 8px',
              cursor: 'pointer',
              fontFamily: "'DM Mono',monospace",
            }}
          >
            Reiniciar
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar tutorial"
            style={{
              border: '1px solid #e3dbce',
              background: '#fff',
              color: '#7b7266',
              borderRadius: 999,
              width: 20,
              height: 20,
              lineHeight: 1,
              fontSize: 12,
              cursor: 'pointer',
              padding: 0,
              fontFamily: "'DM Mono',monospace",
            }}
          >
            ×
          </button>
        </div>
      </div>

      <div style={{
        borderRadius: 8,
        background: doneAll ? '#eef9f2' : '#f8f4ec',
        border: `1px solid ${doneAll ? '#bfe3cc' : '#e7dcc9'}`,
        padding: '7px 8px',
        fontFamily: "'DM Mono',monospace",
        fontSize: 10,
        color: doneAll ? '#2f7a51' : '#806f59',
      }}>
        {doneAll ? '✅ Tutorial completado' : `Progreso: ${doneCount}/${total} pasos`}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.knowsRightClick)}>{checks.knowsRightClick ? '✓' : '1'}</span>
          <span>Haz click derecho en el lienzo para abrir el menú de nodos.</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasRole)}>{checks.hasRole ? '✓' : '2'}</span>
          <span>Añade a alguien de tu equipo (👤).</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasTool)}>{checks.hasTool ? '✓' : '3'}</span>
          <span>Añade una herramienta que uséis (🔧).</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasSoftware)}>{checks.hasSoftware ? '✓' : '4'}</span>
          <span>Añade un software que utilices (💻).</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasChannel)}>{checks.hasChannel ? '✓' : '5'}</span>
          <span>Añade un canal de comunicación (💬).</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasProcess)}>{checks.hasProcess ? '✓' : '6'}</span>
          <span>Crea tu primer proceso con el botón <strong>+ Iniciar proceso</strong>.</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasFlow)}>{checks.hasFlow ? '✓' : '7'}</span>
          <span>Conecta: persona → herramienta → software → comunicación.</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasConnectionVerb)}>{checks.hasConnectionVerb ? '✓' : '8'}</span>
          <span>Click en el paso, describe la conexión usando un verbo.</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasPseudocode)}>{checks.hasPseudocode ? '✓' : '9'}</span>
          <span>Exporta pseudocódigo desde <code>{'{} Pseudocódigo'}</code>.</span>
        </div>
        <div style={stepStyle}>
          <span style={badgeStyle(checks.hasPNG)}>{checks.hasPNG ? '✓' : '10'}</span>
          <span>Exporta PNG desde 🖼️ PNG.</span>
        </div>
      </div>
    </div>
  );
};

export default TutorialSection;
